import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
  PieChart, Pie,
} from 'recharts';

const FORNITORI = [
  { nome: 'Coop. Salute Roma', assegnato: 1800, impegnato: 1200, liquidato: 980 },
  { nome: 'Consorzio ADI Lazio', assegnato: 2200, impegnato: 1500, liquidato: 1100 },
  { nome: 'Fondazione Gemelli@Home', assegnato: 1500, impegnato: 900, liquidato: 720 },
  { nome: 'ASL Roma 2 — Diretto', assegnato: 800, impegnato: 600, liquidato: 500 },
  { nome: 'Coop. Riabilita Roma', assegnato: 1200, impegnato: 750, liquidato: 580 },
];

const EXPENSE_DATA = [
  { name: 'Infermieristica', value: 2800 },
  { name: 'Fisioterapia', value: 1400 },
  { name: 'OSS', value: 980 },
  { name: 'Telemedicina', value: 420 },
  { name: 'Specialistica', value: 600 },
];

const EXPENSE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

const EXPENSE_PIE = [
  { name: 'Infermieristica', value: 2800 },
  { name: 'Fisioterapia', value: 1400 },
  { name: 'OSS', value: 980 },
  { name: 'Telemedicina', value: 420 },
  { name: 'Specialistica', value: 600 },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

const INVOICE_FUNNEL = [
  { fase: 'Ricevute', count: 245 },
  { fase: 'Accettate', count: 218 },
  { fase: 'In Liquidazione', count: 186 },
  { fase: 'Pagate', count: 152 },
];

const FUNNEL_COLORS = ['#64748b', '#3b82f6', '#f59e0b', '#10b981'];

function ExpenseTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{label || payload[0]?.payload?.name}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-500"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color || p.fill }} />{p.name || 'Spesa'}: <strong>€ {p.value?.toLocaleString()}K</strong></p>
      ))}
    </div>
  );
}

function FunnelTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{d.fase}</p>
      <p className="text-slate-500">Fatture: <strong>{d.count}</strong></p>
      <p className="text-slate-400 text-[10px]">{d.fase === 'Ricevute' ? '100%' : `${((d.count / 245) * 100).toFixed(0)}% del totale`}</p>
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  const total = EXPENSE_PIE.reduce((s, e) => s + e.value, 0);
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{d.name}</p>
      <p className="text-slate-500">€ {d.value?.toLocaleString()}K</p>
      <p className="text-slate-400 text-[10px]">{((d.value / total) * 100).toFixed(1)}% del budget</p>
    </div>
  );
}

export default function ASLFinance() {
  const totaleAssegnato = FORNITORI.reduce((s, f) => s + f.assegnato, 0);
  const totaleLiquidato = FORNITORI.reduce((s, f) => s + f.liquidato, 0);
  const totaleImpegnato = FORNITORI.reduce((s, f) => s + f.impegnato, 0);

  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-finance-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Economico-Finanziario</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Budget tracking, distribuzione spesa e stato fatturazione</p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70">Liquidato YTD</p>
                <p className="font-bold text-emerald-700 dark:text-emerald-300">€ {totaleLiquidato.toLocaleString()}K</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
              <TrendingUp className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70">Impegnato</p>
                <p className="font-bold text-amber-700 dark:text-amber-300">€ {totaleImpegnato.toLocaleString()}K</p>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Tracker */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="budget-tracker">
          <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-4">Budget Tracker — Fornitori Accreditati</h2>
          <div className="flex flex-col gap-4">
            {FORNITORI.map(f => {
              const pctImp = (f.impegnato / f.assegnato) * 100;
              const pctLiq = (f.liquidato / f.assegnato) * 100;
              const overBudget = pctImp > 85;
              return (
                <div key={f.nome} className="flex items-center gap-4">
                  <div className="w-48 shrink-0">
                    <p className="text-xs font-semibold text-slate-800 dark:text-white truncate">{f.nome}</p>
                    <p className="text-[10px] text-slate-400">€ {f.assegnato.toLocaleString()}K assegnato</p>
                  </div>
                  <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 flex">
                      <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${pctLiq}%` }} />
                      <div className={`${overBudget ? 'bg-red-400' : 'bg-amber-400'} h-full transition-all duration-500`} style={{ width: `${pctImp - pctLiq}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-[10px]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />€ {f.liquidato}K</span>
                    <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${overBudget ? 'bg-red-400' : 'bg-amber-400'}`} />€ {f.impegnato - f.liquidato}K</span>
                    {overBudget && <Badge className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-0 text-[9px] font-bold px-1.5">{pctImp.toFixed(0)}%</Badge>}
                  </div>
                </div>
              );
            })}
            <div className="flex items-center gap-4 text-[10px] text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Liquidato</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Impegnato</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Oltre 85%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700" /> Disponibile</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Expense Breakdown BarChart with Cell */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="expense-breakdown">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">Distribuzione Spesa per Tipologia</h2>
            <p className="text-[10px] text-slate-400 mb-4">Breakdown per categoria professionale (K€)</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={EXPENSE_DATA} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<ExpenseTooltip />} />
                  <Bar dataKey="value" name="Spesa (K€)" radius={[6, 6, 0, 0]}>
                    {EXPENSE_DATA.map((entry, index) => (
                      <Cell key={`expense-${index}`} fill={EXPENSE_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              {EXPENSE_DATA.map((d, i) => (
                <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: EXPENSE_COLORS[i] }} /> {d.name}
                </span>
              ))}
            </div>
          </div>

          {/* Pie Chart — Composizione Spesa */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="expense-pie">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">Composizione Spesa</h2>
            <p className="text-[10px] text-slate-400 mb-4">Ripartizione percentuale per tipologia</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={EXPENSE_PIE}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {EXPENSE_PIE.map((entry, index) => (
                      <Cell key={`pie-${index}`} fill={PIE_COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-1 pt-3 border-t border-slate-100 dark:border-slate-800">
              {EXPENSE_PIE.map((d, i) => {
                const total = EXPENSE_PIE.reduce((s, e) => s + e.value, 0);
                return (
                  <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }} /> {d.name} ({((d.value / total) * 100).toFixed(0)}%)
                  </span>
                );
              })}
            </div>
          </div>

          {/* Invoice Funnel — Horizontal Bar */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="invoice-funnel">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">Funnel Fatturazione</h2>
              <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-0 text-[10px] font-bold">TMP: 47 giorni</Badge>
            </div>
            <p className="text-[10px] text-slate-400 mb-4">Stato avanzamento ciclo passivo</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={INVOICE_FUNNEL} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis dataKey="fase" type="category" tick={{ fontSize: 10, fill: '#94a3b8' }} width={90} />
                  <Tooltip content={<FunnelTooltip />} />
                  <Bar dataKey="count" name="Fatture" radius={[0, 6, 6, 0]}>
                    {INVOICE_FUNNEL.map((entry, index) => (
                      <Cell key={`funnel-${index}`} fill={FUNNEL_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              {INVOICE_FUNNEL.map((d, i) => (
                <span key={d.fase} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: FUNNEL_COLORS[i] }} /> {d.fase}: {d.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
